import {AfterViewInit, Component, DestroyRef, inject, OnInit, ViewEncapsulation} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ApiService} from "../services/api/api.service";
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from "@angular/cdk/drag-drop";
import {HeaderDataService} from "../services/header_data.service";
import {DateTimePeriodService} from "../services/date-time-period.service";
import {catchError, concatMap, tap} from "rxjs/operators";
import {EMPTY, forkJoin, of} from "rxjs";
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {AppScope} from '../services/app_scope.service';
import {NameOrDescriptionPipe} from '../shared/pipes';
import * as utils from '../lib/utils';
import {deepCopy, pageUrlSegmentFromSnapshot} from '../lib/utils';
import {Tile as TileModel} from "../_models/tile";
import {SingleResponse} from "../services/api/response-types";
import {Series} from '../_models/series';
import {SeriesDataService} from '../services/series_data.service';
import {SeriesTabGroup} from "../forms/series-form-dialog/series-form-dialog.service";
import {DateTimeInstanceService} from "../services/date-time-instance.service";
import {IDMap, KeyMap, ModelName} from "../_typing/generic-types";
import {TileParameters} from "../services/tile_data.service";
import {MatDialog} from "@angular/material/dialog";
import {UserService} from "../services/user.service";
import {addEditPageButton, openStaticPageEditDialog} from "../forms/static-page-form/static-page-edit.utils";
import {SelectManySeriesComponent} from "../components/select-search-api/many/wrappers/select-many-series.component";
import {SharedModule} from "../shared/modules/shared-module.module";
import {PageTileComponent} from "../page-view/page-tile/page-tile.component";
import {WireButtonComponent} from "../shared/buttons/wire-button/wire-button.component";
import {SeriesEditButtonComponent} from "../components/series-edit-button/series-edit-button.component";
import {SeriesPermissions} from "../_typing/series-constant-permissions";
import {difference as _difference} from "lodash-es";

@Component({
    selector: 'quick-charts',
    templateUrl: 'quick-charts.component.html',
    styleUrls: ['../../../node_modules/c3/c3.css', './spc-chart.component.less',
        '../components/select-search/select-search.component.less', './charts.less'],
    imports: [
        SharedModule,
        CdkDropList,
        CdkDrag,
        SelectManySeriesComponent,
        PageTileComponent,
        WireButtonComponent,
        SeriesEditButtonComponent
    ],
    encapsulation: ViewEncapsulation.None
})
export class QuickChartsComponent implements OnInit, AfterViewInit {
    private readonly destroyRef = inject(DestroyRef);

    tabGroups = SeriesTabGroup;
    full_series_list: any[] = [];
    chart_type: { name: string, value: string } = {name: 'Default', value: 'default-chart'};
    config = {
        "hide_comments": false,
        "labels": {
            "sub_title": null,
            "title": null,
        },
        "series_list": [
            {
                "axis": "y",
                "colour": null,
                "cumulative": false,
                "name": null,
                "show_limits": false,
                "type": "line"
            }
        ]
    };
    tile_configs: KeyMap<TileModel> = {};
    chart_types = [
        {name: 'Default', value: 'default-chart'},
        {name: 'SPC', value: 'spc-chart'},
        {name: 'Budget Bar', value: 'budget-bar-chart'},
        // 'Operations', TODO ADD THIS CHART TYPE
        {name: 'Line', value: 'custom-chart'},
        {name: 'Area', value: 'custom-chart'},
        {name: 'Bar', value: 'custom-chart'},
        {name: 'Pie', value: 'custom-chart'},
        {name: 'Multi Line', value: 'custom-chart'},
        {name: 'Multi Area', value: 'custom-chart'},
        {name: 'Multi Bar', value: 'custom-chart'},
    ];

    selected_series: any = [];
    search_series: string[] = [];
    selected_series_names = '';
    forecast: any;
    table_config: any = {};
    configs: KeyMap<TileParameters> = {};
    chart_config: any;
    ready: boolean = false;
    series_ids: any;

    seriesPermissions: IDMap<SeriesPermissions>;
    showAggregationWarning: boolean = false;
    aggregationWarning = 'You do not have permissions to override calculations on some of the selected series: ';
    seriesNotAllowed: ModelName[] = [];

    private _pageUrlSegment: string;

    constructor(private api: ApiService,
                public headerData: HeaderDataService,
                private router: Router,
                private dateTimePeriodService: DateTimePeriodService,
                private activatedRoute: ActivatedRoute,
                protected appScope: AppScope,
                private seriesData: SeriesDataService,
                protected dateInst: DateTimeInstanceService,
                private userService: UserService,
                private dialog: MatDialog,
                private location: Location) {
    }

    ngOnInit(): void {
        const ctrl = this;
        this.selected_series = [];
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this._pageUrlSegment = pageUrlSegmentFromSnapshot(this.activatedRoute);

        forkJoin([this.appScope.auth_complete.promise, this.dateTimePeriodService.dtpInitialisedPromise.promise])
            .pipe(concatMap(() => {
                    const dtp = this.dateTimePeriodService.getPageDtpDefault(this.dateInst.dtp, this._pageUrlSegment);
                    this.dateInst.emitDateTimePeriodChanged(dtp);

                    if (queryParams['series_list']) {
                        this.getConfigFromUrl(queryParams, ctrl.full_series_list);
                        return this.seriesData.getSeriesLightListById(this.search_series).pipe(
                            tap((result) => {
                                    ctrl.selected_series = result?.data;
                                    ctrl.getConfig(false);
                                }
                            ),
                            takeUntilDestroyed(this.destroyRef))

                    } else {
                        ctrl.ready = true;
                        return EMPTY;
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.buildHeader();
                ctrl.ready = true
            })

    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.headerData.show_dtp = true;
            this.headerData.addDownload();
        })
    }

    compare(p1, p2): boolean {
        if (p1 && p2) {
            return p1.name === p2.name;
        }
        return false;
    }

    queueAggregations(): void {
        this.showAggregationWarning = false;
        const seriesIds = this.selected_series.map(series => series.id);
        const isAdmin = this.userService.isAdminUser();
        const allowedSeriesIds = seriesIds.filter(sId => isAdmin ||
            (this.seriesPermissions?.hasOwnProperty(sId) && this.seriesPermissions[sId].includes('override_calculations')));

        const seriesNotAllowed = _difference(seriesIds, allowedSeriesIds);
        this.seriesNotAllowed = seriesNotAllowed.length ? this.selected_series.filter(s => seriesNotAllowed.includes(s.id)).map(s => s.attributes.name) : [];
        if (this.seriesNotAllowed.length) this.showAggregationWarning = true;

        if (!allowedSeriesIds.length) return;
        this.headerData.queueAggregations(this.dateInst.dtp, allowedSeriesIds).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    getConfigFromUrl(params: { series_list?, chart_type? }, full_series_list) {
        const ctrl = this;
        this.search_series = params['series_list'];

        if (this.search_series) {
            if (!Array.isArray(this.search_series)) {
                this.search_series = [this.search_series]
            }

        }
        let search_chart_type = params['chart_type'];
        if (search_chart_type) {
            const chart_type = JSON.parse(search_chart_type);
            ctrl.chart_type = ctrl.chart_types.find(ct => ct.name === chart_type);
        } else {
            ctrl.chart_type = {name: 'Default', value: 'default-chart'};
        }

    }

    getTile(content, params) {
        let tile = new TileModel()
        tile.attributes.content = content;
        tile.attributes.parameters = params;
        return tile;
    }

    getTiles() {
        if (['Multi Line', 'Multi Area', 'Multi Bar', 'Pie', 'Gauge', 'Donut'].includes(this.chart_type.name)) {
            this.tile_configs[this.chart_type.name] = this.getTile(this.chart_type.value, this.configs[this.chart_type.name]);
            return;
        }
        this.selected_series.map(series => {
            if (['Default', 'Budget Bar'].includes(this.chart_type.name)) {
                this.tile_configs[series.id] = this.getTile(this.chart_type.value, {series: series})
            } else if (['Line', 'Bar', 'Area', 'SPC'].includes(this.chart_type.name)) {
                this.tile_configs[series.id] = this.getTile(this.chart_type.value, this.configs[series.id]);
            }
        })
    }

    getConfig(updateUrl = true) {
        const ctrl = this;
        this.showAggregationWarning = false;
        this._getSeriesPermissions();
        if (['Line', 'Bar', 'Area'].includes(ctrl.chart_type.name)) {
            ctrl.selected_series.map(series => {
                this.configs[series.id] = {
                    series_list: [{
                        name: series.attributes.name,
                        axis: 'y',
                        type: this.chart_type.name.toLocaleLowerCase(),
                        show_limits: false,
                        cumulative: false
                    }],
                    labels: {
                        title: series.attributes.description,
                        subtitle: series.attributes.name
                    },
                    chart_type: this.chart_type.name.toLowerCase()
                }
            });
        } else if (['SPC'].includes(ctrl.chart_type.name)) {
            const $obs_list = [];
            //**Limits aren't on series_light so need to be fetched**
            this.selected_series.map(s => {
                const $obs = this.api.series.getById(s.id).pipe(takeUntilDestroyed(this.destroyRef),
                    tap((s_: SingleResponse<Series>) => {
                        const series = s_.data;
                        this.configs[series.id] = {
                            series_list: [{
                                name: series.attributes.name,
                                axis: 'y',
                                type: 'line',
                                show_limits: false,
                                cumulative: false
                            }],
                            //TODO: is this correct or are the limits something else?
                            limits: {
                                hihi: utils.scaleLimits(series.attributes.hihi, ctrl.dateTimePeriodService, series),
                                hi: utils.scaleLimits(series.attributes.hi, ctrl.dateTimePeriodService, series),
                                low: utils.scaleLimits(series.attributes.low, ctrl.dateTimePeriodService, series),
                                lowlow: utils.scaleLimits(series.attributes.lowlow, ctrl.dateTimePeriodService, series),
                                std: series.attributes.std
                            },
                            labels: {
                                title: series.attributes.description,
                                subtitle: series.attributes.name
                            },
                            chart_type: ctrl.chart_type.name.toLowerCase()
                        }
                    }), catchError(e=> {
                        console.log(`Could not load data for series ${s.attributes?.name}: `, e);
                        return of(null);
                    }));
                $obs_list.push($obs);
            });
            if ($obs_list.length < 1) return;
            forkJoin($obs_list).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getTiles());

        } else if (['Multi Line', 'Multi Area', 'Multi Bar', 'Pie', 'Gauge', 'Donut'].includes(this.chart_type.name)) {
            //Get header
            this.selected_series_names = this.selected_series.map(
                item => NameOrDescriptionPipe.prototype.transform(item.attributes)).join(', ');
            const config_series_list = this.selected_series.map(item => {
                let chart_type = this.chart_type;
                let config_type = '';
                switch (chart_type.name) {
                    case 'Pie':
                        config_type = 'pie';
                        break;
                    case 'Donut':
                        config_type = 'donut';
                        break;
                    case 'Gauge':
                        config_type = 'gauge';
                        break;
                    default:
                        config_type = this.chart_type.name.toLocaleLowerCase().substr(6);
                        break
                }
                return {
                    name: item.attributes.name,
                    axis: 'y',
                    type: config_type,
                    show_limits: false,
                    cumulative: false
                }
            });
            this.configs = {
                [this.chart_type.name]: {
                    series_list: config_series_list,
                    labels: {
                        title: ctrl.chart_type
                    }
                }
            }
        }
        if (updateUrl) {
            ctrl.updateURL();
        }
        if (this.chart_type.name === 'SPC') return; //getTiles called inside subscription
        ctrl.getTiles();
    }

    canQueueAggregations() {
        return this.userService.isAdminUser() || this.selected_series.some(s => this.seriesPermissions?.[s.id]?.includes('override_calculations'));
    }

    private _getSeriesPermissions() {
        const seriesIds = this.selected_series?.map(s => s.id).filter(sId => !this.seriesPermissions || !this.seriesPermissions.hasOwnProperty(sId));
        if (!seriesIds?.length) return;
        return this.seriesData.getSeriesPermissions(seriesIds).pipe(tap(permissions => {
            this.seriesPermissions = Object.assign(this.seriesPermissions || {}, permissions);
        }), takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    updateURL() {
        const queryParams = {
            series_list: this.selected_series.map(series => series.id),
            chart_type: JSON.stringify(this.chart_type.name)
        };

        // Update the URL without triggering a navigation (avoids component re-init with onSameUrlNavigation: "reload")
        const urlTree = this.router.createUrlTree([], {
            relativeTo: this.activatedRoute,
            queryParams: queryParams,
            queryParamsHandling: 'merge',
        });
        this.location.replaceState(this.router.serializeUrl(urlTree));
    }

    drop(event: CdkDragDrop<string[]>) {
        const ctrl = this;
        moveItemInArray(this.selected_series, event.previousIndex, event.currentIndex);
    }

    removeSeries(option) {
        this.showAggregationWarning = false;
        let index = this.selected_series.indexOf(option);
        if (index === -1) return;
        this.selected_series.splice(index, 1);
        this.selected_series = [...this.selected_series];
        this.updateURL();
    }

    updateSeries(event, series) {
        if (event && series) {
            series = deepCopy(event);
        }
    }

    private _editPage() {
        openStaticPageEditDialog(this.dialog, this.dateInst, this._pageUrlSegment, 'Edit quick chart defaults');
    }

    buildHeader() {
        this.headerData.show_dtp = true;
        this.headerData.title = 'Quick Charts';
        addEditPageButton(this.headerData, this.userService, this._editPage.bind(this));
    }
}
